import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Papa from 'papaparse';
import { supabase } from '../config/supabase';
import {
  bulkMatchCsvTemplate,
  parseBulkMatchCsv,
  validateBulkMatchRows,
  type BulkMatchPreviewRow,
} from '../utils/bulkMatchImport';

type BulkImportTeam = {
  id: string;
  team_name: string;
  league_configuration_id: string;
  home_facility_id: string | null;
  default_warmup_minutes: number | null;
};

type ExistingMatch = {
  id: string;
  team_id: string;
  opponent_name: string;
  match_date: string;
  groupme_event_id: string | null;
};

type ImportResult = {
  rowNumber: number;
  opponent: string;
  status: 'Imported' | 'Imported with warning' | 'Failed' | 'Skipped';
  error: string;
};

type BulkMatchImportProps = {
  open: boolean;
  teams: BulkImportTeam[];
  facilities: { value: string; label: string }[];
  existingMatches: ExistingMatch[];
  auditUserId: string | null;
  onClose: () => void;
  onImported: () => Promise<void>;
};

function subtractMinutes(time: string, minutesToSubtract: number): string {
  const [hours, minutes] = time.split(':').map(Number);
  const total = (hours * 60 + minutes - minutesToSubtract + 24 * 60) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function downloadCsv(filename: string, rows: Record<string, unknown>[]) {
  const blob = new Blob([Papa.unparse(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function BulkMatchImport({
  open,
  teams,
  facilities,
  existingMatches,
  auditUserId,
  onClose,
  onImported,
}: BulkMatchImportProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [teamId, setTeamId] = useState('');
  const [csvText, setCsvText] = useState(bulkMatchCsvTemplate());
  const [previewRows, setPreviewRows] = useState<BulkMatchPreviewRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [allowDuplicateWarnings, setAllowDuplicateWarnings] = useState(false);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<ImportResult[]>([]);

  const selectedTeam = useMemo(
    () => teams.find((team) => team.id === teamId) || teams[0] || null,
    [teamId, teams],
  );
  const effectiveTeamId = selectedTeam?.id || '';
  const importableRows = previewRows.filter((row) => (
    !row.issues.some((issue) => issue.severity === 'error')
    && (allowDuplicateWarnings || !row.issues.some((issue) => issue.severity === 'warning'))
  ));
  const warningCount = previewRows.filter((row) => row.issues.some((issue) => issue.severity === 'warning')).length;
  const errorCount = previewRows.filter((row) => row.issues.some((issue) => issue.severity === 'error')).length;

  const reset = () => {
    setTeamId(teams[0]?.id || '');
    setCsvText(bulkMatchCsvTemplate());
    setPreviewRows([]);
    setParseErrors([]);
    setAllowDuplicateWarnings(false);
    setResults([]);
  };

  const close = () => {
    if (importing) return;
    onClose();
    reset();
  };

  const buildPreview = async (sourceText = csvText) => {
    if (!selectedTeam) {
      setParseErrors(['Select a team before reviewing the import.']);
      return;
    }
    const parsed = parseBulkMatchCsv(sourceText);
    setParseErrors(parsed.errors);
    setResults([]);
    if (parsed.errors.length > 0) {
      setPreviewRows([]);
      return;
    }
    const [mappedMatchResult, mappedEventResult] = await Promise.all([
      supabase
        .from('match_events')
        .select('id, team_id, opponent_name, match_date, groupme_event_id')
        .is('deleted_at', null),
      supabase
        .from('events')
        .select('groupme_event_id')
        .not('groupme_event_id', 'is', null)
        .is('deleted_at', null),
    ]);
    if (mappedMatchResult.error || mappedEventResult.error) {
      setParseErrors([mappedMatchResult.error?.message || mappedEventResult.error?.message || 'Unable to validate existing GroupMe mappings.']);
      setPreviewRows([]);
      return;
    }
    setPreviewRows(validateBulkMatchRows(parsed.rows, {
      teamId: selectedTeam.id,
      defaultFacilityId: selectedTeam.home_facility_id,
      defaultWarmupMinutes: selectedTeam.default_warmup_minutes ?? 30,
      facilities: facilities.map((facility) => ({ id: facility.value, name: facility.label })),
      existingMatches: ((mappedMatchResult.data || existingMatches) as ExistingMatch[]).map((match) => ({
        id: match.id,
        teamId: match.team_id,
        opponent: match.opponent_name,
        matchDate: match.match_date,
        groupmeEventId: match.groupme_event_id,
      })),
      existingEventGroupMeIds: ((mappedEventResult.data || []) as { groupme_event_id: string | null }[])
        .map((event) => event.groupme_event_id || '')
        .filter(Boolean),
    }));
  };

  const uploadCsv = async (file: File | undefined) => {
    if (!file) return;
    const uploadedText = await file.text();
    setCsvText(uploadedText);
    setPreviewRows([]);
    setParseErrors([]);
    setResults([]);
    await buildPreview(uploadedText);
  };

  const importMatches = async () => {
    if (!selectedTeam || !auditUserId || importableRows.length === 0) {
      if (!auditUserId) setParseErrors(['Your CourtCaptain user profile is required to record bulk-import audit metadata.']);
      return;
    }
    setImporting(true);
    const nextResults: ImportResult[] = [];

    for (const row of previewRows) {
      const hasError = row.issues.some((issue) => issue.severity === 'error');
      const hasWarning = row.issues.some((issue) => issue.severity === 'warning');
      if (hasError || (hasWarning && !allowDuplicateWarnings)) {
        nextResults.push({
          rowNumber: row.rowNumber,
          opponent: row.opponent,
          status: 'Skipped',
          error: hasError ? row.issues.map((issue) => issue.message).join(' ') : 'Duplicate warning was not overridden.',
        });
        continue;
      }

      const { data, error } = await supabase
        .from('match_events')
        .insert({
          team_id: selectedTeam.id,
          league_configuration_id: selectedTeam.league_configuration_id,
          match_classification: row.classification,
          match_site_type: row.siteType,
          match_date: row.matchDate,
          match_time: row.matchTime,
          warmup_time: subtractMinutes(row.matchTime, row.warmupMinutes),
          opponent_name: row.opponent,
          facility_id: row.facilityId,
          match_status: 'Scheduled',
          lineup_status: 'Not Started',
          groupme_event_id: row.groupmeEventId || null,
          notes_summary: row.notes || null,
          creation_source: 'Bulk Import',
          imported_at: new Date().toISOString(),
          imported_by: auditUserId,
          created_by: auditUserId,
          updated_by: auditUserId,
        })
        .select('id, event_id')
        .single();

      if (error || !data) {
        nextResults.push({
          rowNumber: row.rowNumber,
          opponent: row.opponent,
          status: 'Failed',
          error: error?.message || 'Match could not be created.',
        });
        continue;
      }

      let rowError = '';
      if (row.facilityText && data.event_id) {
        const { error: locationError } = await supabase
          .from('events')
          .update({ location_text: row.facilityText, updated_by: auditUserId })
          .eq('id', data.event_id);
        rowError = locationError?.message || '';
      }
      const { error: availabilityError } = await supabase.rpc('initialize_availability_for_match', {
        p_match_event_id: data.id,
        p_created_by: auditUserId,
      });
      rowError ||= availabilityError?.message || '';
      nextResults.push({
        rowNumber: row.rowNumber,
        opponent: row.opponent,
        status: rowError ? 'Imported with warning' : 'Imported',
        error: rowError,
      });
    }

    setResults(nextResults);
    setImporting(false);
    await onImported();
  };

  const importedCount = results.filter((result) => result.status.startsWith('Imported')).length;
  const failedCount = results.filter((result) => ['Failed', 'Skipped'].includes(result.status)).length;
  const importedWarningCount = results.filter((result) => result.status === 'Imported with warning').length;

  return (
    <Dialog open={open} onClose={close} maxWidth="xl" fullWidth>
      <DialogTitle>Bulk Add Matches</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            Select one team, paste or upload a CSV, review the rows, then import. Team season, match format, home facility, and warmup defaults are applied automatically.
          </Alert>
          <FormControl fullWidth required>
            <InputLabel>Team</InputLabel>
            <Select
              label="Team"
              value={effectiveTeamId}
              onChange={(event) => {
                setTeamId(event.target.value);
                setPreviewRows([]);
                setResults([]);
              }}
            >
              {teams.map((team) => <MenuItem key={team.id} value={team.id}>{team.team_name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            label="Paste CSV"
            value={csvText}
            onChange={(event) => {
              setCsvText(event.target.value);
              setPreviewRows([]);
              setResults([]);
            }}
            multiline
            minRows={8}
            fullWidth
            inputProps={{ spellCheck: false }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <input
              ref={fileInputRef}
              hidden
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => void uploadCsv(event.target.files?.[0])}
            />
            <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileInputRef.current?.click()}>
              Upload CSV
            </Button>
            <Button variant="contained" onClick={() => void buildPreview()} disabled={!effectiveTeamId || !csvText.trim()}>
              Review Import
            </Button>
          </Stack>

          {parseErrors.map((error) => <Alert key={error} severity="error">{error}</Alert>)}
          {previewRows.length > 0 && (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`${previewRows.length} rows`} />
                <Chip label={`${errorCount} with errors`} color={errorCount ? 'error' : 'default'} />
                <Chip label={`${warningCount} possible duplicates`} color={warningCount ? 'warning' : 'default'} />
              </Box>
              {warningCount > 0 && (
                <FormControlLabel
                  control={<Checkbox checked={allowDuplicateWarnings} onChange={(event) => setAllowDuplicateWarnings(event.target.checked)} />}
                  label="Import rows with possible duplicate warnings"
                />
              )}
              <TableContainer sx={{ maxHeight: 440, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Table stickyHeader size="small" sx={{ minWidth: 1250 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Opponent</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Site</TableCell>
                      <TableCell>Facility</TableCell>
                      <TableCell>Classification</TableCell>
                      <TableCell>Warmup</TableCell>
                      <TableCell>GroupMe Event ID</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell>Validation</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewRows.map((row) => {
                      const hasError = row.issues.some((issue) => issue.severity === 'error');
                      const hasWarning = row.issues.some((issue) => issue.severity === 'warning');
                      return (
                        <TableRow key={row.rowNumber} sx={{ bgcolor: hasError ? '#fff1f1' : hasWarning ? '#fff8e1' : undefined }}>
                          <TableCell>{row.opponent || '—'}</TableCell>
                          <TableCell>{row.matchDate || 'Invalid'}</TableCell>
                          <TableCell>{row.matchTime || 'Invalid'}</TableCell>
                          <TableCell>{row.siteType}</TableCell>
                          <TableCell>{row.facility || (row.facilityId ? 'Team home facility' : '—')}</TableCell>
                          <TableCell>{row.classification}</TableCell>
                          <TableCell>{row.warmupMinutes} min</TableCell>
                          <TableCell>{row.groupmeEventId || '—'}</TableCell>
                          <TableCell>{row.notes || '—'}</TableCell>
                          <TableCell>
                            {row.issues.length === 0
                              ? <Chip size="small" color="success" label="Ready" />
                              : <Stack spacing={0.5}>{row.issues.map((issue) => <Typography key={issue.message} variant="caption" color={`${issue.severity}.main`}>{issue.message}</Typography>)}</Stack>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {importing && <LinearProgress />}
          {results.length > 0 && (
            <>
              <Alert severity={failedCount > 0 || importedWarningCount > 0 ? 'warning' : 'success'}>
                {importedCount} match{importedCount === 1 ? '' : 'es'} imported
                {failedCount > 0 ? `. ${failedCount} failed or skipped.` : '.'}
                {importedWarningCount > 0 ? ` ${importedWarningCount} imported with a follow-up warning.` : ''}
              </Alert>
              <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>CSV Row</TableCell>
                      <TableCell>Opponent</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((result) => (
                      <TableRow key={result.rowNumber}>
                        <TableCell>{result.rowNumber}</TableCell>
                        <TableCell>{result.opponent || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={result.status}
                            color={result.status === 'Imported' ? 'success' : result.status === 'Imported with warning' ? 'warning' : 'error'}
                          />
                        </TableCell>
                        <TableCell>{result.error || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          {(failedCount > 0 || importedWarningCount > 0) && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => downloadCsv('bulk-match-import-errors.csv', results.filter((result) => result.status !== 'Imported'))}
            >
              Download Error Details
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={importing}>{results.length > 0 ? 'Close' : 'Cancel'}</Button>
        <Button
          variant="contained"
          onClick={() => void importMatches()}
          disabled={importing || importableRows.length === 0 || results.length > 0}
        >
          Import {importableRows.length || ''} Match{importableRows.length === 1 ? '' : 'es'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
